import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Check } from 'lucide-react'

import { api } from '@/lib/api'
import { useWorkspaceStore } from '@/stores/workspace.store'
import type { ApiResponse } from '@/types/api'
import type { NotificationFull } from '@/types/models'

interface Props {
  onClose: () => void
}

/**
 * Formats a date string as a relative time (e.g. "2 hours ago").
 */
function timeAgo(dateStr: string): string {
  try {
    const diff = Date.now() - new Date(dateStr).getTime()
    const secs = Math.floor(diff / 1000)
    if (secs < 60) return 'just now'
    const mins = Math.floor(secs / 60)
    if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`
    const hours = Math.floor(mins / 60)
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
    const days = Math.floor(hours / 24)
    if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`
    const months = Math.floor(days / 30)
    return `${months} month${months === 1 ? '' : 's'} ago`
  } catch {
    return dateStr
  }
}

/**
 * NotificationPanel — dropdown list of notifications.
 *
 * Renders inside NotificationBell's Popover.Content.
 */
export default function NotificationPanel({ onClose }: Props) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const workspaceId = useWorkspaceStore((s) => s.currentWorkspaceId)

  const { data, isLoading } = useQuery<ApiResponse<NotificationFull[]>>({
    queryKey: ['notifications', workspaceId],
    queryFn: () => api.get<ApiResponse<NotificationFull[]>>('/v1/notifications'),
    enabled: !!workspaceId,
  })

  const notifications = data?.data ?? []

  const markReadMutation = useMutation({
    mutationFn: (id: string) =>
      api.patch(`/v1/notifications/${id}/read`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', workspaceId] })
      queryClient.invalidateQueries({ queryKey: ['notifications-unread', workspaceId] })
    },
  })

  const markAllReadMutation = useMutation({
    mutationFn: () => api.post('/v1/notifications/read-all', {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', workspaceId] })
      queryClient.invalidateQueries({ queryKey: ['notifications-unread', workspaceId] })
    },
  })

  function handleItemClick(notification: NotificationFull) {
    if (!notification.isRead) {
      markReadMutation.mutate(notification.id)
    }
    if (notification.actionUrl) {
      navigate(notification.actionUrl)
      onClose()
    }
  }

  const unreadCount = notifications.filter((n) => !n.isRead).length

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b px-4 py-3">
        <h3 className="text-sm font-semibold text-foreground">
          Notifications
          {unreadCount > 0 && (
            <span className="ml-2 rounded-full bg-brand-blue px-2 py-0.5 text-xs text-white">
              {unreadCount}
            </span>
          )}
        </h3>
        {unreadCount > 0 && (
          <button
            type="button"
            onClick={() => markAllReadMutation.mutate()}
            disabled={markAllReadMutation.isPending}
            className="flex items-center gap-1 rounded text-xs text-brand-blue hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
          >
            <Check className="h-3 w-3" />
            Mark all read
          </button>
        )}
      </div>

      {/* List */}
      <div className="max-h-96 overflow-y-auto">
        {isLoading && (
          <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
            Loading...
          </div>
        )}

        {!isLoading && notifications.length === 0 && (
          <div className="flex flex-col items-center justify-center py-8 text-sm text-muted-foreground">
            <p>No notifications yet</p>
          </div>
        )}

        {notifications.map((n) => (
          <button
            key={n.id}
            type="button"
            onClick={() => handleItemClick(n)}
            className={`w-full border-b px-4 py-3 text-left transition-colors hover:bg-accent last:border-b-0 ${
              !n.isRead ? 'bg-brand-blue/5' : ''
            }`}
          >
            <div className="flex items-start gap-3">
              {!n.isRead && (
                <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-brand-blue" />
              )}
              <div className={`min-w-0 flex-1 ${n.isRead ? 'pl-5' : ''}`}>
                <p className="truncate text-sm font-medium text-foreground">{n.title}</p>
                <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">
                  {n.message.length > 80 ? `${n.message.slice(0, 80)}...` : n.message}
                </p>
                <p className="mt-1 text-xs text-muted-foreground/70">
                  {timeAgo(n.createdAt)}
                </p>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}
